/**
 * A SYNESTHESIA press from this device: `POST /synesthesia/fire {command, args}` to one connected computer, which
 * sends it to the PC that drives Synesthesia (OPBridge S344/S352). Used by the Synesthesia Grid and the Synesthesia
 * Button; a timeline carries the same body as a cue and never comes here.
 *
 * ## What the face can honestly say
 *
 * Synesthesia is driven over MIDI, which is one way: nothing ever tells us the scene changed. So nothing here is
 * "on air". OPBridge answers a press AFTER the wait it asked for (the next bar by default), so the press is WAITING
 * (amber) from the tap to that answer, and SENT once OPBridge says it went out (DEC-321: flood = confirmed, SENT = only
 * OPButtons knows). For `next` / `prev` / `random` OPBridge names the scene it picked, so even those show which scene.
 *
 * ## Failures, in words
 *
 * Every refusal comes back as a sentence a performer can act on (`why`), never a status code. A refusal that says the
 * list is stale (control switched off, a scene with no mapping) reads the list again.
 */

import { createSignal } from 'solid-js';
import { getConnection } from '../opbridge/store.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { panicLatched } from '../performance/panicLatch.ts';
import { bridgeHasSynesthesia, noteNotSetUp, refreshSynList, synAskRow, synCommands, synSummary } from './list.ts';
import { isSceneChange, synArgs, synLabel, synProblem, takesCommand, wireCommand, type SynAction, type SynWhen } from './model.ts';

export type SynFireResult = { ok: true; scene?: string } | { ok: false; why: string; quiet?: boolean };

/** What this device has sent, for the faces. Not persisted: a reload starts with nothing marked sent, which is the
 *  honest state (a reload knows nothing about the projector). */
export interface SynSession {
  /** The last scene a press from here sent, by name, and when. Cleared by a move whose scene is not known (home, a
   *  playlist step). */
  lastScene: { name: string; at: number } | null;
  /** The last move that sent no known scene ("Home playlist"), for the grid's status line. */
  lastMove: { label: string; at: number } | null;
  /** Presses waiting for their bar line, by key (a scene name, `cmd:next`...). */
  waiting: Record<string, number>;
  /** Where each meta control was last put from here (0..1), by name, so a slider rests where it was left. */
  values: Record<string, number>;
}
const [session, setSession] = createSignal<SynSession>({ lastScene: null, lastMove: null, waiting: {}, values: {} });
export const synSession = session;

/** The key a press waits under: the scene's name, else the command. */
export function synKey(a: SynAction): string {
  return a.command === 'scene' ? `scene:${a.scene ?? ''}` : a.command === 'meta' ? `meta:${a.control ?? ''}` : `cmd:${a.command}`;
}
export const synWaiting = (a: SynAction): boolean => session().waiting[synKey(a)] !== undefined;
/** Whether `a` is the last scene this device sent. */
export const synSentScene = (name: string | undefined): boolean => !!name && session().lastScene?.name === name;

/** Record a value as put (a slider while it is dragged), before the send returns. */
export function noteSynValue(control: string, value: number): void {
  setSession((s) => ({ ...s, values: { ...s.values, [control]: value } }));
}

function errBody(err: unknown): { error?: string; message?: string } {
  const msg = err instanceof Error ? err.message : String(err);
  const at = msg.indexOf('{');
  if (at < 0) return {};
  try { return JSON.parse(msg.slice(at)) as { error?: string; message?: string }; } catch { return {}; }
}

/** Send `a`, landing `when` (a press: `bar` by default; a slider: `now`). Never throws. */
export async function fireSynesthesia(a: SynAction, when: SynWhen = 'bar'): Promise<SynFireResult> {
  const problem = synProblem(a);
  if (problem) return { ok: false, why: `Nothing sent: ${problem} in the Inspector.` };
  // PANIC LOCKED blocks every send (audit 2026-11-03 A-8); the latch itself is on screen, so this is quiet.
  if (panicLatched()) return { ok: false, why: 'PANIC is locked.', quiet: true };
  const row = synAskRow();
  if (!row || !bridgeHasSynesthesia(row)) {
    const s = synSummary();
    return { ok: false, why: s.text || 'No computer can reach Synesthesia.' };
  }
  const conn = getConnection(row.id);
  if (!conn || conn.status() !== 'connected') return { ok: false, why: `${row.name ?? 'That computer'} is not connected.` };
  const commands = synCommands();
  if (!takesCommand(a.command, commands)) {
    return { ok: false, why: `The OPBridge driving Synesthesia does not take "${synLabel(a)}" yet: update it.` };
  }
  const key = synKey(a);
  const scene = isSceneChange(a);
  // Anything landing on a line shows its wait (a scene, a move, a switch on the bar); a slider goes `now`.
  const waits = when !== 'now';
  if (waits) setSession((s) => ({ ...s, waiting: { ...s.waiting, [key]: Date.now() } }));
  if (a.command === 'meta' && a.control) noteSynValue(a.control, a.value ?? 0);
  try {
    const res = await conn.httpClient().synesthesiaFire(wireCommand(a.command, commands), synArgs(a, when));
    // `scene`, `next`, `prev`, `random` answer `{scene: <name>}`: the scene that was launched.
    const sent = typeof res?.['scene'] === 'string' ? (res['scene'] as string) : a.command === 'scene' ? a.scene : undefined;
    if (scene) {
      setSession((s) => ({
        ...s,
        lastScene: sent ? { name: sent, at: Date.now() } : null,
        lastMove: sent ? s.lastMove : { label: synLabel(a), at: Date.now() },
      }));
    }
    return sent ? { ok: true, scene: sent } : { ok: true };
  } catch (err) {
    const code = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const body = errBody(err);
    if (code === 404 && body.error === 'not_set_up') { noteNotSetUp(); return { ok: false, why: synSummary().text || 'Synesthesia is not set up on any PC.' }; }
    if (code === 409) { void refreshSynList(); return { ok: false, why: 'Synesthesia control was switched off on its PC: switch it on in OPBridge\'s dashboard.' }; }
    if (code === 400 && body.error === 'unknown_command') return { ok: false, why: `The OPBridge driving Synesthesia does not take "${synLabel(a)}" yet: update it.` };
    if (code === 400 && body.message) {
      // OPBridge's own sentence ("no mapping for the scene `X`: add it in OPBridge's Synesthesia panel and write the
      // mappings"); the list may have moved, so read it again.
      void refreshSynList();
      return { ok: false, why: body.message.replace(/`/g, '"') };
    }
    if (code === 401 || code === 403) return { ok: false, why: 'OPBridge has not given this device Synesthesia. Pair it again from Computers.' };
    if (err instanceof OpbridgeError && (err.kind === 'network' || err.kind === 'timeout')) return { ok: false, why: `Could not reach ${row.name ?? 'that computer'}.` };
    return { ok: false, why: err instanceof Error ? err.message : String(err) };
  } finally {
    if (waits) {
      setSession((s) => { const w = { ...s.waiting }; delete w[key]; return { ...s, waiting: w }; });
    }
  }
}

/** Tests and the bot: a fresh session. */
export function resetSynSession(): void {
  setSession({ lastScene: null, lastMove: null, waiting: {}, values: {} });
}
